import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle, 
  Home, 
  FileText, 
  Download,
  Loader2,
  Calendar,
  User,
  FlaskConical,
  DollarSign,
  CreditCard
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SolicitudConfirmacion() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [cotizacion, setCotizacion] = useState(null);
  const [paymentVerified, setPaymentVerified] = useState(false);
  
  const sessionId = searchParams.get('session_id');
  const token = localStorage.getItem('clinica_token');

  useEffect(() => {
    if (!token) {
      navigate('/clinica/login');
      return;
    }
    
    fetchCotizacion();
    
    // If coming from Stripe, verify payment
    if (sessionId) {
      verifyPayment();
    }
  }, [id, sessionId, token]);

  const fetchCotizacion = async () => {
    try {
      const response = await axios.get(`${API}/solicitudes/${id}/cotizacion`);
      setCotizacion(response.data);
    } catch (error) {
      console.error('Error fetching cotizacion:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyPayment = async () => {
    try {
      const response = await axios.get(`${API}/payments/verify/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentVerified(response.data.payment_status === 'paid');
    } catch (error) {
      console.error('Error verifying payment:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#505BB3]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <img src="/amplicare-logo.svg" alt="Amplicare Lab" className="h-9" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Success Header */}
          <div className={`px-6 py-8 text-center ${sessionId && paymentVerified ? 'bg-green-500' : 'bg-[#505BB3]'}`}>
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className={`w-12 h-12 ${sessionId && paymentVerified ? 'text-green-500' : 'text-[#505BB3]'}`} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {sessionId && paymentVerified 
                ? '¡Pago Completado Exitosamente!' 
                : '¡Solicitud Enviada Exitosamente!'}
            </h1>
            <p className="text-white/90">
              {sessionId && paymentVerified 
                ? 'Su pago ha sido procesado correctamente' 
                : 'Hemos recibido su solicitud de pruebas de laboratorio'}
            </p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* ID Banner */}
            {cotizacion && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 text-center">
                <p className="text-blue-600 text-sm font-medium mb-1">ID DE SOLICITUD</p>
                <p className="text-blue-900 text-2xl font-bold font-mono tracking-wider">
                  {cotizacion.id_corto || `SOL-${id.slice(0, 6).toUpperCase()}`}
                </p>
              </div>
            )}
            
            {/* Cotización Details */}
            {cotizacion && (
              <>
                {/* Info Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                      <Calendar className="w-4 h-4" />
                      Fecha
                    </div>
                    <p className="font-semibold text-slate-900">{cotizacion.fecha}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                      <User className="w-4 h-4" />
                      Paciente
                    </div>
                    <p className="font-semibold text-slate-900">{cotizacion.paciente}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                      <FlaskConical className="w-4 h-4" />
                      Pruebas
                    </div>
                    <p className="font-semibold text-slate-900">{cotizacion.items?.length || 0}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                      <DollarSign className="w-4 h-4" />
                      Total
                    </div>
                    <p className="font-semibold text-green-600">${cotizacion.total?.toFixed(2)} MXN</p>
                  </div>
                </div>

                {/* Pruebas List */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-4 py-3 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">Pruebas Solicitadas</h3>
                  </div>
                  <div className="divide-y divide-slate-200">
                    {cotizacion.items?.map((item, index) => (
                      <div key={index} className={`px-4 py-3 ${item.tipo === 'escalonado' ? 'bg-green-50' : ''}`}>
                        {item.tipo === 'escalonado' ? (
                          <div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-green-600">📦</span>
                                <span className="font-medium text-slate-900">{item.nombre}</span>
                              </div>
                              <span className="font-semibold text-green-600">${item.precio?.toFixed(2)}</span>
                            </div>
                            {item.pruebas_incluidas && (
                              <div className="mt-2 pl-6 space-y-1">
                                {item.pruebas_incluidas.map((p, i) => (
                                  <p key={i} className="text-sm text-slate-600">• {p}</p>
                                ))}
                                <p className="text-xs text-green-600 mt-1">
                                  Precio escalonado aplicado
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-slate-900">{item.nombre}</span>
                              {item.codigo && <span className="text-sm text-slate-500 ml-2">({item.codigo})</span>}
                            </div>
                            <span className="font-semibold text-slate-700">${item.precio?.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="bg-slate-50 px-4 py-3 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900">TOTAL</span>
                      <span className="text-xl font-bold text-green-600">${cotizacion.total?.toFixed(2)} MXN</span>
                    </div>
                  </div>
                </div>

                {/* Payment Status Badge */}
                {sessionId && (
                  <div className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg ${
                    paymentVerified 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    <CreditCard className="w-5 h-5" />
                    <span className="font-medium">
                      {paymentVerified 
                        ? 'Pago confirmado con tarjeta' 
                        : 'Verificando pago...'}
                    </span>
                  </div>
                )}

                {/* Next Steps */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Próximos Pasos:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Recibirá un correo de confirmación con los detalles de su solicitud</li>
                    <li>• Nuestro equipo procesará las muestras y realizará las pruebas</li>
                    <li>• Los resultados estarán disponibles en su panel en aproximadamente 24-48 horas</li>
                    <li>• Recibirá una notificación cuando los resultados estén listos</li>
                  </ul>
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                onClick={() => navigate('/clinica/dashboard')}
                className="flex-1 h-12 bg-[#505BB3] hover:bg-[#404A9C] text-white"
              >
                <Home className="w-4 h-4 mr-2" />
                Volver al Panel
              </Button>
              <Button
                variant="outline"
                onClick={() => window.print()}
                className="flex-1 h-12 border-slate-300"
              >
                <Download className="w-4 h-4 mr-2" />
                Imprimir Comprobante
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
